import { type MenuInputOption } from '@saviynt/design-system';

// ============================================================================
// Option Validation Utilities
// ============================================================================

/**
 * Validates if an object is a valid MenuInputOption
 */
export const isValidMenuInputOption = (
  option: unknown
): option is MenuInputOption =>
  option != null &&
  typeof option === 'object' &&
  'value' in option &&
  'label' in option &&
  option.value !== undefined &&
  option.value !== null &&
  option.label !== undefined &&
  option.label !== null;

/**
 * Validates and sanitizes an array of MenuInputOption objects.
 * Coerces `label` and `value` to strings so the DS MenuInput sort (localeCompare) never crashes.
 */
export const validateOptions = (
  optionsToValidate: MenuInputOption[]
): MenuInputOption[] => {
  if (!Array.isArray(optionsToValidate)) {
    return [];
  }

  return optionsToValidate.filter(isValidMenuInputOption).map((opt) => ({
    ...opt,
    label: String(opt.label),
    value: String(opt.value),
  }));
};

/**
 * Validates and ensures selectedOptions always contains valid MenuInputOption objects.
 * Coerces `label` and `value` to strings so the DS MenuInput sort (localeCompare) never crashes.
 */
export const validateSelectedOptions = (
  optionsToValidate: MenuInputOption[]
): MenuInputOption[] => {
  if (!Array.isArray(optionsToValidate)) {
    return [];
  }

  return optionsToValidate.filter(isValidMenuInputOption).map((opt) => ({
    ...opt,
    label: String(opt.label),
    value: String(opt.value),
  }));
};

/**
 * Validates and sanitizes API response data to ensure all options are valid.
 * Coerces `label` and `value` to strings so the DS MenuInput sort (localeCompare) never crashes.
 */
export const validateApiResponseData = (
  responseData: unknown
): MenuInputOption[] => {
  const rawData = Array.isArray(responseData)
    ? (responseData as MenuInputOption[])
    : [];

  return rawData.filter(isValidMenuInputOption).map((opt) => ({
    ...opt,
    label: String(opt.label),
    value: String(opt.value),
  }));
};

/**
 * Reads the paginated `data` array from a data-provider inner payload, whether the
 * API returns a bare array or `{ data: [...], defaultSelection?: [...] }`.
 */
export function extractDataProviderOptionRows(
  responseData: unknown
): unknown[] {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (
    responseData &&
    typeof responseData === 'object' &&
    'data' in responseData &&
    Array.isArray((responseData as { data: unknown }).data)
  ) {
    return (responseData as { data: unknown[] }).data;
  }

  return [];
}

/**
 * Reads optional `defaultSelection` from the same inner payload (update flows
 * when `valuesToSearch` is sent).
 */
export function extractDataProviderDefaultSelection(
  responseData: unknown
): unknown[] {
  if (!responseData || typeof responseData !== 'object') {
    return [];
  }

  const ds = (responseData as Record<string, unknown>).defaultSelection;

  return Array.isArray(ds) ? ds : [];
}

/**
 * Parses the inner data-provider preview payload into validated option arrays.
 * Keeps call sites (e.g. SingleSelect) from tripping typed-eslint when nested
 * under casts around {@link useGetDataProvider}.
 */
export function parsePreviewDataProviderInnerPayload(rawInner: unknown): {
  baseRows: MenuInputOption[];
  defaultRows: MenuInputOption[];
} {
  return {
    baseRows: validateApiResponseData(extractDataProviderOptionRows(rawInner)),
    defaultRows: validateApiResponseData(
      extractDataProviderDefaultSelection(rawInner)
    ),
  };
}

/**
 * Reads the inner preview payload from an {@link ApiResponse}-shaped envelope.
 * Shared by form preview hooks and Datastore configuration (avoids importing hooks from UI that create cycles).
 */
export function parseDataProviderPreviewOptionTables(response: unknown): {
  baseRows: MenuInputOption[];
  defaultRows: MenuInputOption[];
} {
  if (!response || typeof response !== 'object') {
    return { baseRows: [], defaultRows: [] };
  }

  const outer = Reflect.get(response, 'data') as unknown;
  const rawInner: unknown =
    outer && typeof outer === 'object' && 'data' in outer
      ? Reflect.get(outer as object, 'data')
      : undefined;

  return parsePreviewDataProviderInnerPayload(rawInner);
}
